import React, { useState, useEffect } from 'react';
import { parseServerTime } from '@/lib/serverTime';
import { useLocation, useNavigate } from 'react-router-dom';
import { receiptInfoApi, ReceiptInfo } from '@/api/planApi';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { ActiveSessions } from './ActiveSessions';
import { TeamManagement } from './TeamManagement';
import { PageContainer, PageHeader } from '@/components/layout';
import { PageTitle } from '@/components/common/PageTitle';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Save, Key, AlertCircle, CheckCircle2, Eye, EyeOff, FileText, ExternalLink, HelpCircle } from 'lucide-react';
import { PASSWORD_REGEX, PASSWORD_RULE_MESSAGE } from '@/lib/validation';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { isProvisionalEmail } from '@/lib/socialAuth';
import { LoadingBlock, Spinner } from '@/components/ui/spinner';

const API_BASE_URL = `${BASE}/api`;

interface WithdrawResponse {
  userId: string;
  withdrawnAt: string;
  scheduledDeletionAt: string;
  reJoinableAt?: string;
  message?: string;
}

interface WithdrawApiResponse {
  success?: boolean;
  data?: WithdrawResponse;
  message?: string;
}

interface TeamStatusResponse {
  isTeamMember: boolean;
  teamId?: number;
  role?: 'OWNER' | 'MEMBER' | 'ADMIN';
  teamName?: string;
  teamPlan?: string;
  /** 팀의 활성 멤버 수 - 오너 본인만 남은 팀인지 판단용 */
  activeMemberCount?: number;
}

const getAuthHeaders = () => {
  // authToken 키로 localStorage와 sessionStorage 모두 확인
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  
  console.log('🔑 토큰 확인:', {
    hasToken: !!token,
    tokenLength: token?.length,
    tokenPreview: token ? token.substring(0, 20) + '...' : 'null'
  });
  
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();

  // URL hash에서 탭 값 읽기 (기본값: profile)
  const getTabFromHash = () => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['profile', 'receipt', 'team'];
    return validTabs.includes(hash) ? hash : 'profile';
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromHash());
  
  // hash 변경 시 탭 업데이트
  useEffect(() => {
    setActiveTab(getTabFromHash());
  }, [location.hash]);
  
  // 탭 변경 시 URL hash 업데이트
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`${location.pathname}#${value}`, { replace: true });
  };
  
  const loginTypeLabelMap = {
    GOOGLE: 'Google',
    NAVER: '네이버',
    KAKAO: '카카오',
  } as const;
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [receiptInfo, setReceiptInfo] = useState<ReceiptInfo | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    receiptInfoApi.get()
      .then(res => {
        const info = res.data;
        if (info && info.receiptType) {
          setReceiptInfo({
            receiptType: info.receiptType,
            cashReceiptPurpose: info.cashReceiptPurpose ?? null,
            phoneNumber: info.cashReceiptPurpose === 'PERSONAL_DEDUCTION' ? (info.cashReceiptNumberMasked ?? null) : null,
            cashReceiptBizNo: info.cashReceiptPurpose === 'BUSINESS_EXPENSE' ? (info.cashReceiptNumberMasked ?? null) : null,
            taxInvoiceBizNo: info.bizRegNoMasked ?? null,
            companyName: info.bizName ?? null,
            ceoName: info.bizCeoName ?? null,
            invoiceEmail: info.taxInvoiceEmail ?? null,
          });
        }
      })
      .catch(() => setReceiptInfo(null))
      .finally(() => setReceiptLoading(false));
  }, []);
  const queryParams = new URLSearchParams(location.search);
  const hasSocialEmailRequiredParam = queryParams.get('socialEmailRequired') === '1';
  const isSocialLoginUser = user?.loginType === 'GOOGLE' || user?.loginType === 'NAVER' || user?.loginType === 'KAKAO';
  const isSocialProvisionalEmail = isSocialLoginUser && isProvisionalEmail(user?.email);
  const socialEmailNeedsUpdate = hasSocialEmailRequiredParam || isSocialProvisionalEmail;
  const socialLoginLabel = user?.loginType && user.loginType !== 'LOCAL'
    ? loginTypeLabelMap[user.loginType]
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!user?.userId) {
      setError('사용자 정보를 찾을 수 없습니다.');
      return;
    }

    if (isSocialProvisionalEmail && isProvisionalEmail(formData.email)) {
      setError('실제 사용 중인 이메일을 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      console.log('📤 프로필 업데이트 요청:', {
        userId: user.userId,
        userApprovalStatus: user?.approvalStatus,  // ← 추가
        userRole: user?.userRole,                  // ← 추가
        endpoint: `${API_BASE_URL}/users/${user.userId}`,
        data: {
          ...formData,
          name: isSocialLoginUser ? user.name : formData.name,
          email: isSocialLoginUser && !isSocialProvisionalEmail ? user.email : formData.email,
        },
        hasAuthHeader: !!getAuthHeaders().Authorization
      });

      const profileUpdateData = {
        ...formData,
        name: isSocialLoginUser ? user.name : formData.name,
        email: isSocialLoginUser && !isSocialProvisionalEmail ? user.email : formData.email,
      };
      
      const response = await axios.put(`${API_BASE_URL}/users/${user.userId}`, profileUpdateData, {
        headers: getAuthHeaders(),
      });

      console.log('✅ 프로필 업데이트 응답:', response.data);

      if (response.data.success) {
        setSuccess('정보가 수정되었습니다.');
        updateUser(profileUpdateData);

        if (response.data.emailVerificationRequired) {
          setSuccess('정보가 수정되었습니다. 새 이메일로 인증 메일이 발송되었습니다.');
        }
      } else {
        setError(response.data.message || '정보 수정에 실패했습니다.');
        console.error('❌ 프로필 업데이트 실패:', response.data);
      }
    } catch (err: any) {
      console.error('❌ 프로필 업데이트 에러:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.message || '정보 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (isSocialLoginUser) {
      setError(`${socialLoginLabel || '소셜'} 계정으로 로그인한 사용자는 비밀번호를 변경할 수 없습니다.`);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    if (!PASSWORD_REGEX.test(passwordForm.newPassword)) {
      setError(PASSWORD_RULE_MESSAGE);
      return;
    }

    setLoading(true);

    try {
      // Backend API: POST /api/auth/change-password
      // 백엔드 구현 필요:
      // - Endpoint: POST /api/auth/change-password
      // - Authorization: Bearer {token}
      // - Request: { currentPassword, newPassword }
      // - Response: { success: boolean, message: string }
      console.log('🔒 비밀번호 변경 요청:', {
        endpoint: `${API_BASE_URL}/auth/change-password`,
        hasAuthHeader: !!getAuthHeaders().Authorization
      });
      
      const response = await axios.post(`${API_BASE_URL}/auth/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      }, {
        headers: getAuthHeaders(),
      });

      console.log('✅ 비밀번호 변경 응답:', response.data);

      if (response.data.success) {
        setSuccess('비밀번호가 변경되었습니다.');
        setShowPasswordModal(false);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
      } else {
        setError(response.data.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="내 정보 수정" />
      <PageContainer maxWidth="full">
      <PageHeader
        title={menuName || '내 정보 수정'}
        description="회원 정보 및 팀을 관리할 수 있습니다"
        icon={menuIcon}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="profile">프로필 수정</TabsTrigger>
          <TabsTrigger value="receipt">증빙 설정</TabsTrigger>
          <TabsTrigger value="team">팀 관리</TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="mt-4">
          <TeamManagement />
        </TabsContent>

        <TabsContent value="receipt" className="mt-4">
          <div className="space-y-4">
            {receiptLoading ? (
              <LoadingBlock size="md" />
            ) : !receiptInfo || receiptInfo.receiptType === 'NONE' ? (
              <div className="border rounded-xl p-8 text-center space-y-3 bg-gray-50">
                <FileText className="w-10 h-10 mx-auto text-gray-300" />
                <p className="text-sm font-medium text-gray-600">
                  {receiptInfo?.receiptType === 'NONE'
                    ? '증빙을 발행안함으로 설정하셨습니다.'
                    : '등록된 증빙 발행 정보가 없습니다.'}
                </p>
                <Button size="sm" variant="outline" onClick={() => navigate('/dashboard/my/receipt-info')}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  지금 설정하기
                </Button>
              </div>
            ) : (
              <div className="border rounded-xl overflow-hidden">
                {/* 헤더 */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold">등록된 증빙 설정</span>
                  </div>
                  <button
                    onClick={() => navigate('/dashboard/my/receipt-info')}
                    className="flex items-center gap-1 text-xs text-primary font-medium hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    설정 변경
                  </button>
                </div>

                {/* 본문 */}
                <div className="divide-y">
                  <div className="flex justify-between items-center px-4 py-3 text-sm">
                    <span className="text-gray-500 w-28 flex-shrink-0">증빙 유형</span>
                    <span className="font-semibold text-right">
                      {receiptInfo.receiptType === 'CASH_RECEIPT' ? '현금영수증' : '세금계산서'}
                    </span>
                  </div>

                  {receiptInfo.receiptType === 'CASH_RECEIPT' && (
                    <>
                      <div className="flex justify-between items-center px-4 py-3 text-sm">
                        <span className="text-gray-500 w-28 flex-shrink-0">용도</span>
                        <span className="font-medium text-right">
                          {receiptInfo.cashReceiptPurpose === 'PERSONAL_DEDUCTION' ? '개인 소득공제용' : '사업자 지출증빙용'}
                        </span>
                      </div>
                      {receiptInfo.cashReceiptPurpose === 'PERSONAL_DEDUCTION' && receiptInfo.phoneNumber && (
                        <div className="flex justify-between items-center px-4 py-3 text-sm">
                          <span className="text-gray-500 w-28 flex-shrink-0">휴대폰번호</span>
                          <span className="font-medium font-mono text-right">{receiptInfo.phoneNumber}</span>
                        </div>
                      )}
                      {receiptInfo.cashReceiptPurpose === 'BUSINESS_EXPENSE' && receiptInfo.cashReceiptBizNo && (
                        <div className="flex justify-between items-center px-4 py-3 text-sm">
                          <span className="text-gray-500 w-28 flex-shrink-0">사업자번호</span>
                          <span className="font-medium font-mono text-right">{receiptInfo.cashReceiptBizNo}</span>
                        </div>
                      )}
                    </>
                  )}

                  {receiptInfo.receiptType === 'TAX_INVOICE' && (
                    <>
                      {receiptInfo.taxInvoiceBizNo && (
                        <div className="flex justify-between items-center px-4 py-3 text-sm">
                          <span className="text-gray-500 w-28 flex-shrink-0">사업자번호</span>
                          <span className="font-medium font-mono text-right">{receiptInfo.taxInvoiceBizNo}</span>
                        </div>
                      )}
                      {receiptInfo.companyName && (
                        <div className="flex justify-between items-center px-4 py-3 text-sm">
                          <span className="text-gray-500 w-28 flex-shrink-0">상호명</span>
                          <span className="font-medium text-right">{receiptInfo.companyName}</span>
                        </div>
                      )}
                      {receiptInfo.ceoName && (
                        <div className="flex justify-between items-center px-4 py-3 text-sm">
                          <span className="text-gray-500 w-28 flex-shrink-0">대표자명</span>
                          <span className="font-medium text-right">{receiptInfo.ceoName}</span>
                        </div>
                      )}
                      {receiptInfo.invoiceEmail && (
                        <div className="flex justify-between items-center px-4 py-3 text-sm">
                          <span className="text-gray-500 w-28 flex-shrink-0">발행 이메일</span>
                          <span className="font-medium text-right">{receiptInfo.invoiceEmail}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* 증빙 설정 안내 */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" />
                  증빙 설정 안내
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">증빙 설정이란?</h3>
                  <p className="text-gray-600 text-xs">유료 요금제를 이용하실 때 결제 금액에 대한 세금계산서 또는 현금영수증을 발행받을 수 있도록 증빙 정보를 설정하는 기능입니다.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">증빙 유형</h3>
                  <div className="space-y-1 text-gray-600 text-xs">
                    <p>• <span className="font-medium">현금영수증:</span> 개인 소득공제용 또는 사업자 지출증빙용으로 발행</p>
                    <p>• <span className="font-medium">세금계산서:</span> 사업자 등록번호로 발행 (법인/사업자용)</p>
                    <p>• <span className="font-medium">발행안함:</span> 증빙 발행 없이 이용</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">설정 시기</h3>
                  <p className="text-gray-600 text-xs">유료 요금제로 업그레이드 전 또는 결제 후 즉시 설정하시면 됩니다. 설정하지 않으면 기본적으로 '발행안함'으로 처리됩니다.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-1">설정 변경</h3>
                  <p className="text-gray-600 text-xs">증빙 정보는 언제든지 변경할 수 있습니다. 변경된 정보는 다음 결제부터 적용됩니다.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-4">
          <div className="space-y-4">

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {socialEmailNeedsUpdate && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            {socialLoginLabel || '소셜'} 계정의 이메일 확인이 필요합니다. 실제 이메일로 변경하면 인증 메일이 발송됩니다.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>기본 정보</CardTitle>
            <CardDescription>이름, 이메일, 연락처 등</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">아이디</label>
                <Input type="text" value={user?.userId} disabled className="bg-gray-100" />
                <p className="text-xs text-gray-500 mt-1">아이디는 변경할 수 없습니다</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={loading || isSocialLoginUser}
                  className={isSocialLoginUser ? 'bg-gray-100' : undefined}
                />
                {isSocialLoginUser && (
                  <p className="text-xs text-gray-500 mt-1">
                    {socialLoginLabel} 계정 사용자는 이름을 변경할 수 없습니다.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  이메일 <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={loading || (isSocialLoginUser && !isSocialProvisionalEmail)}
                  className={
                    isSocialLoginUser && !isSocialProvisionalEmail
                      ? 'bg-gray-100'
                      : isSocialProvisionalEmail
                        ? 'bg-amber-50 border-amber-300'
                        : undefined
                  }
                />
                {!isSocialLoginUser && formData.email !== user?.email && (
                  <p className="text-xs text-yellow-600 mt-1">
                    이메일 변경 시 재인증이 필요합니다
                  </p>
                )}
                {isSocialLoginUser && !isSocialProvisionalEmail && (
                  <p className="text-xs text-gray-500 mt-1">
                    {socialLoginLabel} 계정 사용자는 이메일을 변경할 수 없습니다.
                  </p>
                )}
                {isSocialProvisionalEmail && (
                  <p className="text-xs text-amber-700 mt-1">
                    현재 계정은 임시 이메일 상태입니다. 실제 이메일로 변경하면 인증 메일이 발송됩니다.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">전화번호</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="010-1234-5678"
                  disabled={loading}
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    저장 중...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    저장
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>보안 설정</CardTitle>
              <CardDescription>비밀번호 변경</CardDescription>
            </CardHeader>
            <CardContent>
              {isSocialLoginUser ? (
                <p className="text-sm text-gray-600">
                  {socialLoginLabel} 계정으로 로그인한 사용자는 비밀번호를 변경할 수 없습니다.
                </p>
              ) : (
                <Button onClick={() => navigate('/dashboard/profile/change-password')} variant="outline" className="w-full">
                  <Key className="w-4 h-4 mr-2" />
                  비밀번호 변경
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>계정 정보</CardTitle>
              <CardDescription>수정 불가한 정보</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">권한</p>
                <p className="font-semibold">
                  {user?.userRole === 'SUPER_ADMIN' ? '관리자' : '일반 회원'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">가입일</p>
                <p className="font-semibold">
                  {user?.createdAt ? parseServerTime(user.createdAt)?.toLocaleDateString('ko-KR') : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">마지막 로그인</p>
                <p className="font-semibold">
                  {user?.lastLoginAt ? parseServerTime(user.lastLoginAt)?.toLocaleString('ko-KR') : '-'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>기타 설정</CardTitle>
              <CardDescription>추가 설정 옵션</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">이용가이드 다시 보기</p>
                  <p className="text-xs text-gray-500">로그인 시 이용가이드를 다시 표시합니다</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    localStorage.removeItem('userGuideDontShowAgain');
                    window.location.href = '/dashboard';
                  }}
                >
                  다시 보기
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>비밀번호 변경</CardTitle>
              <CardDescription>새로운 비밀번호를 입력해주세요</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    현재 비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                    }
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    새 비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                      }
                      required
                      disabled={loading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* 실시간 비밀번호 조건 체크 */}
                  <ul className="mt-2 space-y-1">
                    {[
                      { label: '10자 이상', ok: passwordForm.newPassword.length >= 10 },
                      { label: '대문자 포함', ok: /[A-Z]/.test(passwordForm.newPassword) },
                      { label: '소문자 포함', ok: /[a-z]/.test(passwordForm.newPassword) },
                      { label: '숫자 포함', ok: /\d/.test(passwordForm.newPassword) },
                      { label: '특수문자 포함 (@$!%*#?&)', ok: /[@$!%*#?&]/.test(passwordForm.newPassword) },
                      { label: '허용되지 않은 문자 없음 (영문·숫자·@$!%*#?& 만 허용)', ok: passwordForm.newPassword.length > 0 && /^[A-Za-z\d@$!%*#?&]+$/.test(passwordForm.newPassword) },
                    ].map(({ label, ok }) => (
                      <li
                        key={label}
                        className={`text-xs flex items-center gap-1 ${
                          passwordForm.newPassword.length === 0
                            ? 'text-gray-400'
                            : ok
                            ? 'text-green-600'
                            : 'text-red-500'
                        }`}
                      >
                        {passwordForm.newPassword.length === 0 ? '•' : ok ? '✓' : '✗'} {label}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    새 비밀번호 확인 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                      }
                      required
                      disabled={loading}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordForm.confirmPassword && (
                    <p className={`text-xs mt-1 ${
                      passwordForm.newPassword === passwordForm.confirmPassword ? 'text-green-600' : 'text-red-500'
                    }`}>
                      {passwordForm.newPassword === passwordForm.confirmPassword
                        ? '✓ 비밀번호가 일치합니다'
                        : '✗ 비밀번호가 일치하지 않습니다'}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button type="submit" disabled={loading || !PASSWORD_REGEX.test(passwordForm.newPassword)} className="flex-1">
                    {loading ? <Spinner size="sm" /> : '변경'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                      setPasswordForm({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                      });
                    }}
                    variant="outline"
                    className="flex-1"
                    disabled={loading}
                  >
                    취소
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Sessions Management */}
      <ActiveSessions />
          </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
    </>
  );
};

export const Withdraw: React.FC = () => {
  const { user, logout } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [termsOfService, setTermsOfService] = useState<string>('');
  // 탈퇴 성공 직후 "즉시 취소(Undo)" 화면으로 전환하기 위한 상태.
  // 탈퇴 API 성공 응답의 JWT는 아직 살아있으므로, 이 시점에만 /me/restore로
  // 되돌릴 수 있다 (로그아웃 후 재로그인은 계정 비활성화로 막혀 있어 불가능).
  const [withdrawResult, setWithdrawResult] = useState<{ reJoinableAt?: string } | null>(null);
  const [undoLoading, setUndoLoading] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/terms/service`, { headers: getAuthHeaders() })
      .then(res => setTermsOfService(res.data.content))
      .catch(() => setTermsOfService(''));
  }, []);

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!confirmed) {
      setError('탈퇴 안내를 확인하고 동의해주세요.');
      return;
    }

    if (user?.loginType === 'LOCAL' && !password) {
      setError('비밀번호를 입력해주세요.');
      return;
    }

    // 팀 소속 상태 확인
    // - MEMBER: 기존과 동일하게 사전 차단 (팀을 먼저 나가야 함)
    // - OWNER: 백엔드가 "혼자 남은 팀은 자동 해지 후 탈퇴 허용, 다른 팀원이
    //   있으면 차단"으로 판단하므로, activeMemberCount로 미리 같은 기준을 적용
    let soleOwnerTeamName: string | null = null;
    try {
      const teamStatusRes = await axios.get<TeamStatusResponse>(`${API_BASE_URL}/users/me/team-status`, {
        headers: getAuthHeaders()
      });
      const teamStatus = teamStatusRes.data;
      if (teamStatus.isTeamMember) {
        if (teamStatus.role === 'OWNER') {
          const memberCount = teamStatus.activeMemberCount ?? 0;
          if (memberCount > 1) {
            setError(
              `팀 "${teamStatus.teamName}"에 다른 팀원이 있어 탈퇴할 수 없습니다.\n` +
              `팀장 이전 후 탈퇴해 주세요.`
            );
            return;
          }
          // 혼자 남은 팀의 오너 - 탈퇴 시 팀도 함께 자동 해지됨을 확인창에서 안내
          soleOwnerTeamName = teamStatus.teamName ?? null;
        } else {
          setError(
            `현재 "${teamStatus.teamName}" 팀에 소속되어 있습니다.\n` +
            `팀을 먼저 나간 후 회원 탈퇴가 가능합니다.`
          );
          return;
        }
      }
    } catch (err: any) {
      // 팀 상태 확인 실패 시 진행 (기존 동작 유지)
    }

    const confirmMessage = soleOwnerTeamName
      ? `정말로 탈퇴하시겠습니까? 소속된 팀 "${soleOwnerTeamName}"도 함께 자동 해지됩니다.\n` +
        `탈퇴 후 1년간 데이터가 보관되며, 1년 후 완전히 삭제됩니다.`
      : '정말로 탈퇴하시겠습니까? 탈퇴 후 1년간 데이터가 보관되며, 1년 후 완전히 삭제됩니다.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.delete<WithdrawResponse | WithdrawApiResponse>(`${API_BASE_URL}/users/me`, {
        headers: getAuthHeaders(),
        data: {
          password: user?.loginType === 'LOCAL' ? password : undefined,
          reason: reason || undefined,
        },
      });
      const responsePayload = response.data as WithdrawResponse | WithdrawApiResponse;
      if ((responsePayload as WithdrawApiResponse).success === false) {
        setError((responsePayload as WithdrawApiResponse).message || '회원 탈퇴에 실패했습니다.');
        return;
      }
      const withdrawData = (responsePayload as WithdrawApiResponse)?.data
        ? (responsePayload as WithdrawApiResponse).data
        : (responsePayload as WithdrawResponse);

      // 여기서 바로 로그아웃하지 않고 "즉시 취소" 화면을 보여준다 (아래 handleUndo 참고)
      setWithdrawResult({ reJoinableAt: withdrawData?.reJoinableAt });
    } catch (err: any) {
      setError(err.response?.data?.message || '회원 탈퇴에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 탈퇴 직후, 아직 살아있는 JWT로 즉시 탈퇴를 취소한다.
  const handleUndo = async () => {
    setUndoError(null);
    setUndoLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/users/me/restore`, {
        password: user?.loginType === 'LOCAL' ? password : undefined,
      }, {
        headers: getAuthHeaders(),
      });
      const payload = res.data as { success?: boolean; message?: string };
      if (payload.success === false) {
        setUndoError(payload.message || '탈퇴 취소에 실패했습니다.');
        return;
      }
      setWithdrawResult(null);
      setConfirmed(false);
      setPassword('');
      navigate('/dashboard');
    } catch (err: any) {
      setUndoError(
        err.response?.data?.message ||
        '탈퇴 취소에 실패했습니다. 잠시 후 다시 시도하거나 로그인 화면으로 이동해 주세요.'
      );
    } finally {
      setUndoLoading(false);
    }
  };

  // "취소하지 않고 로그아웃" - 기존 탈퇴 완료 흐름
  const handleProceedToLogout = async () => {
    const queryParams = new URLSearchParams({ withdrawn: '1' });
    if (withdrawResult?.reJoinableAt) {
      queryParams.set('reJoinableAt', withdrawResult.reJoinableAt);
    }
    await logout();
    sessionStorage.removeItem('redirectAfterLogin');
    window.location.href = `/login?${queryParams.toString()}`;
  };

  if (withdrawResult) {
    return (
      <PageContainer maxWidth="full">
        <PageHeader
          title="회원 탈퇴 완료"
          description="탈퇴가 정상적으로 처리되었습니다"
          icon={menuIcon}
        />

        {undoError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{undoError}</AlertDescription>
          </Alert>
        )}

        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle>실수로 탈퇴하셨나요?</CardTitle>
            <CardDescription>
              지금 이 화면에서 바로 취소하면 계정과 팀/카드/구독 상태를 그대로 되돌릴 수 있습니다.
              이 화면을 벗어나 로그인 화면으로 이동하면, 이후에는 이 계정으로 다시 로그인할 수 없어
              스스로 탈퇴를 취소할 방법이 없으니 신중히 선택해 주세요.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={handleUndo} disabled={undoLoading} className="flex-1">
                {undoLoading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    취소 처리 중...
                  </>
                ) : (
                  '지금 취소하기'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleProceedToLogout}
                disabled={undoLoading}
                className="flex-1"
              >
                로그인 화면으로 이동
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '회원 탈퇴'}
        description="탈퇴 시 모든 데이터가 삭제됩니다"
        icon={menuIcon}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-600">⚠️ 탈퇴 전 꼭 확인하세요</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">•</span>
                <span>탈퇴 시 생성한 모든 ALT 텍스트 데이터가 삭제됩니다.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">•</span>
                <span>탈퇴 후 1년간 데이터가 보관되며, 1년 후 완전히 삭제됩니다.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">•</span>
                <span>탈퇴 후 30일 동안 동일 이메일로 재가입할 수 없습니다.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">•</span>
                <span>등록된 카드(자동결제)와 이용 중인 유료 구독은 탈퇴 즉시 해지됩니다. 탈퇴 직후 화면에서 바로 취소하지 않는 한 복구되지 않습니다.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">•</span>
                <span>미납된 결제 건이 있는 경우 탈퇴가 제한될 수 있습니다. 결제를 완료한 후 다시 시도해 주세요.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">•</span>
                <span>팀에 소속된 경우: 팀원은 팀을 먼저 나가야 하고, 다른 팀원이 있는 팀의 팀장은 팀장 이전이 필요합니다. 혼자 남은 팀의 팀장은 탈퇴 시 팀도 함께 자동 해지됩니다.</span>
              </li>
            </ul>
          </div>

          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-medium mb-2">이용약관</h4>
            <p className="text-sm text-gray-700 whitespace-pre-line">
              {termsOfService || '약관을 불러오는 중입니다...'}
            </p>
          </div>

          <form onSubmit={handleWithdraw} className="space-y-4">
            {user?.loginType === 'LOCAL' && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  비밀번호 확인 <span className="text-red-500">*</span>
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="본인 확인을 위해 비밀번호를 입력하세요"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">탈퇴 사유 (선택)</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full min-h-[100px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="탈퇴 사유를 입력해주세요 (선택사항)"
                disabled={loading}
              />
            </div>

            <div className="pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={loading}
                  className="w-4 h-4"
                />
                <span className="text-sm">
                  위 안내사항을 모두 확인했으며, 회원 탈퇴에 동의합니다.
                </span>
              </label>
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={loading || !confirmed}
                variant="destructive"
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    처리 중...
                  </>
                ) : (
                  '회원 탈퇴'
                )}
              </Button>
              <Button
                type="button"
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="flex-1"
                disabled={loading}
              >
                취소
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </PageContainer>
  );
};
